import React from "react";
import PropTypes from "prop-types";
import {
  Typography,
  Box,
  IconButton,
  Chip,
  List,
  ListItem,
  Button,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import GlassCard from "../../../components/common/GlassCard";

function GoalsCard({ goals, onAddGoal, onDeleteGoal, onToggleGoal, onEditGoal }) {
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [activeGoalId, setActiveGoalId] = React.useState(null);

  const handleMenuOpen = (event, id) => {
    setMenuAnchor(event.currentTarget);
    setActiveGoalId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveGoalId(null);
  };

  const handleDelete = (goalId) => {
    handleMenuClose();
    if (onDeleteGoal) {
      onDeleteGoal(goalId);
    }
  };

  const handleEdit = (goal) => {
    handleMenuClose();
    if (onEditGoal) {
      onEditGoal(goal);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "achieved":
        return "success";
      case "in_progress":
        return "primary";
      case "not_started":
        return "default";
      case "discontinued":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "achieved":
        return "Achieved";
      case "in_progress":
        return "In Progress";
      case "not_started":
        return "Not Started";
      case "discontinued":
        return "Discontinued";
      default:
        return status;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "error";
      case "medium":
        return "warning";
      case "low":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <GlassCard
      title="Patient Goals"
      addLabel="Add Goal"
      onAdd={onAddGoal}
      data-testid="goals-card"
      data-card-category="goals"
    >
      {!goals || goals.length === 0 ? (
        <Box textAlign="center" py={3}>
          <Typography variant="body2" color="text.secondary">
            No goals documented
          </Typography>
        </Box>
      ) : (
        <List dense>
          {goals.map((goal) => (
            <ListItem
              key={goal.id}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                mb: 1,
                flexDirection: "column",
                alignItems: "flex-start",
                bgcolor: "background.paper",
              }}
              data-testid={`goal-item-${goal.id}`}
              data-goal-status={goal.status}
            >
              <Box display="flex" justifyContent="space-between" width="100%" mb={1}>
                <Box display="flex" alignItems="center" gap={1} flex={1}>
                  <Tooltip title={goal.status === "achieved" ? "Mark incomplete" : "Mark complete"}>
                    <IconButton
                      size="small"
                      onClick={() => onToggleGoal(goal.id)}
                      color={goal.status === "achieved" ? "success" : "default"}
                      data-testid={`goal-toggle-${goal.id}`}
                      data-action="toggle"
                      aria-label={`${goal.status === "achieved" ? "Mark goal incomplete" : "Mark goal complete"}`}
                    >
                      {goal.status === "achieved" ? (
                        <CheckCircleIcon aria-label={`${goal.status === "achieved" ? "Mark goal incomplete" : "Mark goal complete"}`} />
                      ) : (
                        <RadioButtonUncheckedIcon aria-label={`${goal.status === "achieved" ? "Mark goal incomplete" : "Mark goal complete"}`} />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Typography
                    variant="body2"
                    sx={{
                      textDecoration: goal.status === "achieved" ? "line-through" : "none",
                      flex: 1,
                    }}
                    data-testid={`goal-description-${goal.id}`}
                    data-field-name="goal_description"
                  >
                    {goal.goal_description}
                  </Typography>
                </Box>
                <Box display="flex" gap={0.5}>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(goal)}
                      data-testid={`goal-edit-${goal.id}`}
                      data-action="edit"
                      aria-label={"Edit goal"}
                    >
                      <EditIcon aria-label={"Edit goal"} fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, goal.id)}
                      data-testid={`goal-menu-${goal.id}`}
                      aria-label="Goal options menu"
                    >
                      <MoreVertIcon aria-label="Goal options menu" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box display="flex" gap={1} flexWrap="wrap" width="100%">
                {goal.priority && (
                  <Chip
                    label={goal.priority}
                    size="small"
                    color={getPriorityColor(goal.priority)}
                    sx={{ textTransform: "capitalize" }}
                    data-testid={`goal-priority-${goal.id}`}
                    data-field-name="priority"
                  />
                )}
                <Chip
                  label={getStatusLabel(goal.status)}
                  size="small"
                  color={getStatusColor(goal.status)}
                  data-testid={`goal-status-${goal.id}`}
                  data-field-name="status"
                />
                {goal.target_date && (
                  <Chip
                    label={`Target: ${new Date(goal.target_date).toLocaleDateString()}`}
                    size="small"
                    variant="outlined"
                    data-testid={`goal-target-date-${goal.id}`}
                    data-field-name="target_date"
                  />
                )}
                {goal.category && (
                  <Chip
                    label={goal.category}
                    size="small"
                    variant="outlined"
                    sx={{ textTransform: "capitalize" }}
                    data-testid={`goal-category-${goal.id}`}
                    data-field-name="category"
                  />
                )}
              </Box>

              {goal.notes && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  {goal.notes}
                </Typography>
              )}
            </ListItem>
          ))}
        </List>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            const goal = goals?.find((g) => g.id === activeGoalId);
            if (goal) {
              handleEdit(goal);
            }
          }}
          data-testid={`goal-menu-edit-${activeGoalId}`}
          data-action="edit"
          aria-label="Edit goal"
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => handleDelete(activeGoalId)}
          data-testid={`goal-menu-delete-${activeGoalId}`}
          data-action="delete"
          aria-label="Delete goal"
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

GoalsCard.propTypes = {
  goals: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      goal_description: PropTypes.string.isRequired,
      category: PropTypes.string,
      priority: PropTypes.string,
      status: PropTypes.string.isRequired,
      target_date: PropTypes.string,
      notes: PropTypes.string,
    })
  ),
  onAddGoal: PropTypes.func.isRequired,
  onDeleteGoal: PropTypes.func.isRequired,
  onToggleGoal: PropTypes.func.isRequired,
  onEditGoal: PropTypes.func.isRequired,
};

GoalsCard.defaultProps = {
  goals: [],
};

export default GoalsCard;
